import { Controller } from '@hotwired/stimulus';
import { loadScript } from '@paypal/paypal-js';
import Rails from '@rails/ujs';
import * as Sentry from '@sentry/browser';
import { colors } from '../utils/colors';
import { toast } from '../utils/toast';
export default class default_1 extends Controller {
    static targets = ['card', 'form', 'submit', 'paypalButton', 'loader'];
    static values = {
        stripeSetupTokenPath: String,
        paypalSetupToken: String,
        paypalIdToken: String,
        paypalClientId: String,
        stripeProcessorSerialized: String,
        paypalProcessorSerialized: String,
        currency: String,
        country: String,
        checkoutId: String,
        userEmail: String,
        amount: Number,
    };
    submitEventListener = this.submit.bind(this);
    async connect() {
        if (!window.Stripe) {
            this.loadStripe().then(() => {
                this.initializeStripe();
            });
        }
        else {
            this.initializeStripe();
        }
        if (this.isPaypalEnabled) {
            this.initializePaypal();
        }
    }
    loadStripe() {
        return new Promise((resolve, reject) => {
            const scriptElement = document.createElement('script');
            scriptElement.src = `https://js.stripe.com/v3/`;
            scriptElement.onload = () => resolve();
            scriptElement.onerror = () => reject();
            document.head.appendChild(scriptElement);
        });
    }
    async initializePaypal() {
        loadScript({ clientId: this.paypalClientIdValue }).then((paypal) => {
            paypal
                ?.Buttons?.({
                locale: 'en_US',
                style: {
                    color: 'gold',
                    label: 'pay',
                    height: 54,
                },
                // @ts-ignore
                createVaultSetupToken: () => this.paypalSetupTokenValue,
                // @ts-ignore
                onApprove: ({ vaultSetupToken }) => {
                    this.paypalButtonTarget.classList.add('disabled');
                    this.handlePaymentMethod(this.paypalProcessorSerializedValue, vaultSetupToken);
                },
                onClick: () => {
                    this.paypalButtonTarget.classList.add('disabled');
                },
                onCancel: () => {
                    this.paypalButtonTarget.classList.remove('disabled');
                },
                onError: (error) => {
                    this.paypalButtonTarget.classList.remove('disabled');
                    Sentry.captureException(error);
                },
            })
                .render(this.paypalButtonTarget)
                .then(() => {
                // this.paypalWalletSelectorTarget.classList.remove('hidden')
                // The PayPal button will be rendered in an html element with the ID
                // 'paypal-button'. This function will be called when the PayPal button
                // is set up and ready to be used
            });
        });
    }
    initializeStripe() {
        const stripeKey = document.querySelector('meta[name="stripe-key"]')?.getAttribute('content');
        if (!stripeKey)
            throw new Error('Stripe key not found');
        this.stripe = Stripe(stripeKey, { locale: 'en' });
        this.elements = this.stripe.elements({
            // @ts-ignore this is not typed in @types/stripe-v3
            mode: 'subscription',
            locale: 'en',
            paymentMethodTypes: ['card'],
            setupFutureUsage: 'off_session',
            currency: this.currencyValue?.toLowerCase() || 'usd',
            amount: this.amountValue,
            appearance: getStripeElementsAppearance(),
            loader: 'never',
            customPaymentMethods: this.isPaypalEnabled
                ? [
                    {
                        id: this.paypalCustomPaymentMethodId,
                        options: { type: 'static' },
                    },
                ]
                : undefined,
        });
        this.element.addEventListener('submit', this.submitEventListener);
        // @ts-ignore this is not typed in @types/stripe-v3
        this.payment = this.elements.create('payment', {
            layout: {
                type: 'accordion',
                radios: false,
                spacedAccordionItems: true,
                visibleAccordionItemsCount: 0,
            },
            business: {
                name: 'Subscription',
            },
            fields: {
                billingDetails: {
                    name: 'auto',
                    email: 'never',
                    phone: 'auto',
                    address: 'auto',
                },
            },
        });
        this.payment?.mount(this.cardTarget);
        this.payment?.on('ready', () => {
            this.hideLoader();
            this.submitTarget.disabled = false;
        });
        this.payment?.on('change', (e) => {
            // @ts-ignore
            if (e?.value.type === this.paypalCustomPaymentMethodId) {
                this.showPaypalButton();
            }
            else {
                this.hidePaypalButton();
            }
        });
    }
    async fetchSetupToken() {
        const formData = Object.fromEntries(new FormData(this.formTarget));
        const recaptchaResponseCheckout = formData['g-recaptcha-response-data[checkout]'];
        const recaptchaResponse = formData['g-recaptcha-response'];
        const response = await fetch(this.stripeSetupTokenPathValue, {
            method: 'POST',
            body: JSON.stringify({
                'g-recaptcha-response-data': {
                    checkout: recaptchaResponseCheckout,
                },
                'g-recaptcha-response': recaptchaResponse,
                checkoutId: this.checkoutIdValue,
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            return null;
        }
        return await response.json();
    }
    updateAmount(amount) {
        // @ts-ignore
        this.elements?.update({ amount });
    }
    async submit(event) {
        event.preventDefault();
        event.stopPropagation();
        // @ts-ignore
        const elementsSubmission = await this.elements.submit();
        if (elementsSubmission.error)
            return;
        this.submitTarget.disabled = true;
        const setupTokens = await this.fetchSetupToken();
        if (!setupTokens)
            return (this.submitTarget.disabled = false);
        // @ts-ignore this is not typed in @types/stripe-v3
        const result = await this.stripe?.confirmSetup({
            elements: this.elements,
            clientSecret: setupTokens.setupIntentClientSecret,
            confirmParams: {
                return_url: window.location.href,
                payment_method_data: {
                    billing_details: {
                        email: this.userEmailValue,
                    },
                },
            },
            redirect: 'if_required',
        });
        if (result?.error) {
            this.submitTarget.disabled = false;
            return toast(result?.error?.message ?? 'An error occurred');
        }
        this.handlePaymentMethod(this.stripeProcessorSerializedValue, result.setupIntent.payment_method);
    }
    handlePaymentMethod(processor, payment_method_token) {
        this.element.removeEventListener('submit', this.submitEventListener);
        this.addHiddenField('parameters[processor]', processor);
        this.addHiddenField('parameters[payment_method_token]', payment_method_token);
        Rails.fire(this.formTarget, 'submit');
        this.submitTarget.disabled = true;
    }
    addHiddenField(name, value) {
        let hiddenInput = document.createElement('input');
        hiddenInput.setAttribute('type', 'hidden');
        hiddenInput.setAttribute('name', name);
        hiddenInput.setAttribute('value', value);
        this.formTarget.appendChild(hiddenInput);
    }
    hideLoader() {
        this.loaderTarget.classList.add('hidden');
    }
    showPaypalButton() {
        this.submitTarget.classList.add('hidden');
        this.paypalButtonTarget.classList.remove('hidden');
    }
    hidePaypalButton() {
        this.submitTarget.classList.remove('hidden');
        this.paypalButtonTarget.classList.add('hidden');
    }
    get paypalCustomPaymentMethodId() {
        return {
            production: 'cpmt_1RnDGxLgTfxsYzuGO6Xwyrke',
            staging: 'cpmt_1RnDHpLgTfxsYzuGeclKpDgM',
            development: 'cpmt_1Rzy1CAueuVlHrCvduQMiZtD',
        }[window.__fw_app__.environment];
    }
    get isPaypalEnabled() {
        return this.paypalSetupTokenValue && this.paypalIdTokenValue;
    }
}
const getStripeElementsAppearance = () => {
    const inputCornerRadius = getComputedStyle(document.documentElement).getPropertyValue('--input-corner-radius');
    const fontFamilyBase = getComputedStyle(document.documentElement).getPropertyValue('--font-family-base');
    const fontWeightBase = getComputedStyle(document.documentElement).getPropertyValue('--font-weight-base');
    const buttonCornerRadius = getComputedStyle(document.documentElement).getPropertyValue('--button-corner-radius');
    return {
        variables: {
            fontFamily: fontFamilyBase,
            colorPrimary: colors.onBackground,
            colorBackground: colors.background,
            colorText: colors.onBackground,
            colorTextSecondary: colors.onBackground,
            colorTextPlaceholder: colors.textPale,
            colorDanger: 'red',
            borderRadius: inputCornerRadius,
            fontWeightNormal: fontWeightBase,
            fontWeightMedium: fontWeightBase,
            accordionItemSpacing: '12px',
            pickerItemSpacing: '12px',
            gridColumnSpacing: '16px',
            gridRowSpacing: '16px',
        },
        rules: {
            '.Block': {
                boxShadow: 'none',
            },
            '.Action': {
                textDecoration: 'underline',
            },
            '.Link': {
                color: colors.textPale,
            },
            '.TermsLink': {
                color: 'inherit',
            },
            '.TermsLink:hover': {
                color: colors.onBackground,
            },
            '.PickerItem': {
                borderColor: colors.borderPale,
                boxShadow: 'none',
            },
            '.PickerItem:hover': {
                backgroundColor: colors.background,
                borderColor: colors.onBackground,
                boxShadow: 'none',
            },
            '.PickerItem--selected': {
                borderColor: colors.onBackground,
                boxShadow: 'none',
                borderWidth: '2px',
            },
            '.PickerItem:focus-visible': {
                outline: 'none',
            },
            '.PickerItem--highlight': {
                borderColor: colors.onBackground,
                boxShadow: 'none',
                borderWidth: '2px',
            },
            '.PickerItem--highlight:hover': {
                backgroundColor: colors.background,
                borderColor: colors.onBackground,
                boxShadow: 'none',
                borderWidth: '2px',
            },
            '.PickerItem--selected:hover': {
                borderColor: colors.primary,
                boxShadow: 'none',
            },
            '.PickerAction': {
                fontWeight: '700',
                fontSize: '16px',
                padding: '18px 12px',
                borderRadius: buttonCornerRadius,
                backgroundColor: colors.backgroundPale,
                color: colors.onBackground,
                textDecoration: 'none',
            },
            '.PickerAction:hover': {
                color: colors.onBackground,
                backgroundColor: colors.backgroundPale,
            },
            '.PickerAction:active': {
                color: colors.onBackground,
                backgroundColor: colors.backgroundPale,
            },
            '.Label': {
                fontSize: '0',
                marginBottom: '0',
                lineHeight: '0',
            },
            '.AccordionItem': {
                fontSize: '16px',
                fontWeight: '400',
                lineHeight: '24px',
                padding: '15px',
                borderColor: colors.borderPale,
                boxShadow: 'none',
            },
            '.AccordionItem:hover': {
                borderColor: colors.onBackground,
                boxShadow: 'none',
            },
            '.AccordionItem--selected': {
                borderColor: colors.primary,
                boxShadow: 'none',
                borderWidth: '2px',
            },
            '.AccordionItem:focus-visible	': {
                outline: 'none',
            },
            '.AccordionItem--selected:hover': {
                borderColor: colors.primary,
                boxShadow: 'none',
            },
            '.Input': {
                borderRadius: inputCornerRadius,
                backgroundColor: colors.backgroundPale,
                borderColor: 'transparent',
                fontSize: '16px',
                lineHeight: '24px',
                padding: '16px 16px',
                boxShadow: 'none',
            },
            '.Input:focus': {
                boxShadow: 'none',
                borderColor: colors.textPale,
            },
            '.Input--invalid': {
                borderWidth: '1px',
                boxShadow: 'none',
                color: colors.onBackground,
            },
            '.Input--invalid:focus': {
                borderColor: 'red',
            },
            '.Error': {
                padding: '4px 6px',
                marginTop: '4px',
                fontSize: '14px',
                lineHeight: '20px',
                color: 'white',
                backgroundColor: 'red',
                borderRadius: inputCornerRadius,
            },
            '.MenuAction': {
                fontWeight: '700',
                fontSize: '16px',
                borderRadius: buttonCornerRadius,
                backgroundColor: colors.backgroundPale,
                color: colors.onBackground,
            },
            '.MenuAction:hover': {
                color: colors.onBackground,
                backgroundColor: colors.backgroundPale,
            },
            '.MenuAction:active': {
                color: colors.onBackground,
                backgroundColor: colors.backgroundPale,
            },
            '.MenuAction--danger': {
                color: colors.onBackground,
            },
            '.MenuIcon': {
                borderRadius: buttonCornerRadius,
                backgroundColor: colors.backgroundPale,
                fill: colors.onBackground,
            },
            '.MenuIcon:hover': {
                backgroundColor: colors.backgroundPale,
                fill: colors.onBackground,
            },
            '.MenuIcon:active': {
                backgroundColor: colors.backgroundPale,
                fill: colors.onBackground,
            },
            '.TermsText': {
                color: colors.textPale,
            },
        },
    };
};
